<template>
  <div class="rg-detail-panel">
    <button
      class="close-btn"
      @click="$emit('close', feature.properties.objectId)"
    >
      <span class="material-icons">close</span>
    </button>

    <div class="content-row">
      <div class="image-container">
        <div v-if="isLoading" class="loader"></div>

        <div v-else-if="imgUrl && !loadError" class="thumbnail-wrapper">
          <img
            ref="thumbnail"
            :src="imgUrl"
            class="thumbnail"
            @load="handleImageLoad"
            @error="handleImageError"
          />
          <button class="expand-btn" @click="openExpandModal">
            <span class="material-icons">zoom_in</span>
          </button>
        </div>

        <div v-else class="no-image">画像はありません</div>
      </div>

      <!-- モーダル部分 -->
      <portal to="modal-root" v-if="isExpandModalOpen">
        <div class="expand-modal" @click.self="closeExpandModal">
          <img :src="imgUrl" class="expanded-image" :style="expandStyle" />
          <button class="expand-close-btn" @click="closeExpandModal">
            <span class="material-icons">close</span>
          </button>
        </div>
      </portal>

      <div class="rg-info">
        <div class="rg-info-row">
          <div class="rg-info-label">物体種別</div>
          <div class="rg-info-value">{{ recognitionRate }}</div>
        </div>
        <div class="rg-info-row">
          <div class="rg-info-label">速度</div>
          <div class="rg-info-value">{{ sog }} kt</div>
        </div>
        <div class="rg-info-row">
          <div class="rg-info-label">針路</div>
          <div class="rg-info-value">{{ cog }}°</div>
        </div>
        <div class="rg-info-row">
          <div class="rg-info-label">緯度</div>
          <div class="rg-info-value">{{ latitude }}</div>
        </div>
        <div class="rg-info-row">
          <div class="rg-info-label">経度</div>
          <div class="rg-info-value">{{ longitude }}</div>
        </div>
        <div class="rg-info-row">
          <div class="rg-info-label">撮影日時</div>
          <div class="rg-info-value">{{ shootingDate }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RGU from "../../../../../enterprise/live-map/layers/radar-grid/utils/RadarGridObjectUtil.js";
import { radarGridStorageRef } from "@/firebase/firebaseConfig";
import moment from "moment-timezone";
import * as GeoUtil from "@/utils/GeoUtil";
import Vue from "vue";

export default {
  name: "RgDetailPanel",
  props: {
    feature: { type: Object, required: true },
    nowTime: { type: Number, required: true },
  },
  data() {
    return {
      imgUrl: "",
      loadError: false,
      lastImgPath: "",
      isLoading: false,
      isExpandModalOpen: false,
      expandStyle: {},
      expandAnimating: false,
      expandScale: 0.6,
    };
  },
  watch: {
    nowTime: {
      immediate: true,
      handler(newTime) {
        this.updateImageByNowTime();
      },
    },
  },
  computed: {
    trackNumber() {
      return (
        this.feature.properties.trackNumber ||
        this.feature.properties.name ||
        this.feature.properties.objectId
      );
    },

    sog() {
      const props = this.feature && this.feature.properties;
      if (props && typeof props.sog === "number") {
        return props.sog.toFixed(1);
      }
      return "---";
    },

    cog() {
      const props = this.feature && this.feature.properties;
      if (props && typeof props.cog === "number") {
        return props.cog.toFixed(1);
      }
      return "---";
    },

    recognitionRate() {
      const kindNameJa = RGU.getObjectKindJa(this.feature.properties.kind);
      const rate =
        `${Math.floor(this.feature.properties.confidence * 100)}` || " --- ";

      return `${kindNameJa} (${rate}%)`;
    },

    latitude() {
      return this.feature.properties.lat != null
        ? GeoUtil.formatLatitude(this.feature.properties.lat)
        : "---";
    },
    longitude() {
      return this.feature.properties.lon != null
        ? GeoUtil.formatLongitude(this.feature.properties.lon)
        : "---";
    },

    shootingDate() {
      let momentObjJst = moment(this.feature.properties.shootingDate, "X").tz(
        "Asia/Tokyo"
      );
      return momentObjJst.format("YYYY/MM/DD/ HH:mm") || " --- ";
    },
  },
  methods: {
    async updateImageByNowTime() {
      const currentImgPath = this.feature.properties.imgUrl;

      if (!currentImgPath) {
        this.imgUrl = "";
        this.loadError = true;
        this.isLoading = false;
        return;
      }

      if (this.lastImgPath === currentImgPath) {
        return;
      }

      try {
        this.isLoading = true;
        const downloadUrl = await radarGridStorageRef
          .child(currentImgPath)
          .getDownloadURL();
        this.imgUrl = downloadUrl;
        this.loadError = false;
        this.lastImgPath = currentImgPath;
      } catch (e) {
        Vue.$log.error("画像取得失敗", e);
        this.imgUrl = "";
        this.loadError = true;
        this.lastImgPath = currentImgPath;
      } finally {
        this.isLoading = false;
      }
    },

    handleImageLoad() {
      this.isLoading = false;
    },

    handleImageError() {
      this.isLoading = false;
      this.loadError = true;
    },

    openExpandModal() {
      const thumb = this.$refs.thumbnail;
      if (!thumb) return;

      const rect = thumb.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const targetWidth = vw * this.expandScale;
      const targetHeight = vh * this.expandScale;

      // 初期位置（サムネイル位置から始まる）
      this.expandStyle = {
        position: "fixed",
        top: rect.top + "px",
        left: rect.left + "px",
        width: rect.width + "px",
        height: rect.height + "px",
        transform: "scale(1)",
        transition: "all 0.4s ease",
        zIndex: 10000,
      };

      this.isExpandModalOpen = true;

      this.$nextTick(() => {
        setTimeout(() => {
          this.expandStyle = {
            ...this.expandStyle,
            top: (vh - targetHeight) / 2 + "px",
            left: (vw - targetWidth) / 2 + "px",
            width: targetWidth + "px",
            height: targetHeight + "px",
            transition: "all 0.4s ease",
          };
        }, 10);

        // ★ resizeイベントは handleWindowResize を使う（こっちは transition: none）
        window.addEventListener("resize", this.handleWindowResize);
      });
    },

    handleWindowResize() {
      if (!this.isExpandModalOpen) return;

      const vw = window.innerWidth;
      const vh = window.innerHeight;

      const targetWidth = vw * this.expandScale;
      const targetHeight = vh * this.expandScale;

      // 即時更新用：transition なし
      this.expandStyle = {
        ...this.expandStyle,
        top: (vh - targetHeight) / 2 + "px",
        left: (vw - targetWidth) / 2 + "px",
        width: targetWidth + "px",
        height: targetHeight + "px",
        transform: "scale(1)",
        transition: "none",
      };
    },

    closeExpandModal() {
      const thumb = this.$refs.thumbnail;
      if (!thumb) {
        this.isExpandModalOpen = false;
        return;
      }

      const rect = thumb.getBoundingClientRect();

      this.expandStyle = {
        ...this.expandStyle,
        top: rect.top + "px",
        left: rect.left + "px",
        width: rect.width + "px",
        height: rect.height + "px",
        transform: "scale(1)",
        transition: "all 0.4s ease",
      };

      setTimeout(() => {
        this.isExpandModalOpen = false;

        // ★ブラウザリサイズ監視解除
        window.removeEventListener("resize", this.handleWindowResize);
      }, 400);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";

.rg-detail-panel {
  position: absolute;
  padding: 10px;
  z-index: 10;
  background-color: rgba(2, 20, 49, 0.85);
  color: #fff;
  border-radius: 6px;
  width: 468px;
  height: 160px;
}

.close-btn {
  position: absolute;
  top: 4px;
  right: 6px;
  background: #222;
  color: #fff;
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  padding: 4px 4px;
  cursor: pointer;
  .material-icons {
    font-size: 16px;
  }
}
.close-btn:hover {
  background: #333;
}

.content-row {
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: flex-start;
}

.image-container {
  width: 240px;
  height: 135px;
  background-color: #111;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  flex-shrink: 0;
}

.thumbnail {
  max-width: 100%;
  max-height: 100%;
  border-radius: 4px;
}

.thumbnail-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.expand-btn {
  position: absolute;
  bottom: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  border-radius: 4px;
  padding: 4px 4px;
  cursor: pointer;
  font-size: 16px;
  width: 32px;
  height: 32px;
}

.expand-btn:hover {
  background: rgba(0, 0, 0, 0.7);
}

.expand-close-btn {
  position: absolute;
  top: 24px;
  right: 24px;
  background: #222;
  color: #fff;
  border: none;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  cursor: pointer;
}

.expand-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.expanded-image {
  object-fit: fill;
  border-radius: 8px;
  transition: all 0.4s ease;
}

.no-image {
  font-size: 12px;
  color: $aisea-white;
  font-family: $aisea-font-family;
}

.rg-info {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 2px;
  min-width: 120px;
}

.rg-info-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px; /* ラベルと値の間の隙間 */
}

.rg-info-label {
  font-size: 12px;
  width: 60px;
  color: $aisea-mouse-gray;
  font-family: $aisea-font-family;
  font-weight: 400;
}

.rg-info-value {
  font-size: 14px;
  color: $aisea-white;
  font-family: $aisea-font-family;
  font-weight: 400;
}

.loader {
  border: 4px solid #b0b0b0;
  border-top: 4px solid #f3f3f3;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
